/**
 * Keeps the on-device agent running while the desktop app is open.
 *
 * NevTan's promise is that models run on the machine the app is installed on,
 * and that only happens when an agent is connected: the API dispatches
 * downloads and inference to a paired device, and refuses when there is none.
 *
 * Pairing itself happens when someone signs in, because it needs a person to
 * approve the machine. This only starts what is already paired. An earlier
 * version registered a device here on every sign-in, which is how one laptop
 * came to hold four entries and the server kept choosing a dead one.
 */
import { Command, type Child } from '@tauri-apps/api/shell'
import { isDesktopApp } from './platform'

const SIDECAR = 'binaries/nevtan-agent'

/** The running agent, so a second sign-in does not start a second one. */
let running: Child | null = null
let starting: Promise<void> | null = null

/** Whether this machine already holds an identity. Contacts nothing. */
async function isPaired(): Promise<boolean> {
  const output = await Command.sidecar(SIDECAR, ['status']).execute()
  return output.code === 0
}

/**
 * Starts the agent if this machine is paired. Safe to call on every sign-in.
 *
 * A machine that is not paired is left alone rather than pairing itself:
 * connecting a device needs a person to approve it, and doing that
 * unprompted would open a browser someone did not ask for.
 */
export async function ensureAgentRunning(): Promise<void> {
  if (!isDesktopApp || running) return
  if (starting) return starting

  starting = (async () => {
    if (!(await isPaired())) return

    const command = Command.sidecar(SIDECAR, ['run'])
    // If the agent exits, forget it, so the next sign-in starts a fresh one
    // rather than assuming a dead process is still serving requests.
    command.on('close', () => {
      running = null
    })
    running = await command.spawn()
  })()

  try {
    await starting
  } finally {
    starting = null
  }
}

/** Stops the agent, so no orphan keeps a connection open. */
export async function stopAgent(): Promise<void> {
  const child = running
  running = null
  if (child) {
    await child.kill().catch(() => undefined)
  }
}
